import React from 'react';
import {Table, Tag, Tooltip, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {ReportFileType} from "../graphql/queries/getReportFileListingForCentral";
import dayjs from 'dayjs';
import {ReportFileTypeEnum, ReportFileStatusEnum} from "../../../../enums/ReportFileEnum";
import { useTranslation } from 'react-i18next';

interface CentralReportColumnsProps {
    reportFileData: ReportFileType[];
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: ReportFileType[]) => void;
    selectedRowKeys: React.Key[];
    loading: boolean;
}

const CentralReportFileColumns: React.FC<CentralReportColumnsProps> = ({
    reportFileData,
    onSelectChange,
    selectedRowKeys,
    loading,
}) => {
    const {t} = useTranslation();
    const getReportFileTypeTag = (type: number) => {
        switch (type) {
            case ReportFileTypeEnum.PICKUP_APPOINTMENT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.pickupAppointmentDownload')}</Tag>;
            case ReportFileTypeEnum.PICKUP_PROGRESS:
                return <Tag color="processing">{t('enum.reportTypeOptions.pickupProgressDownload')}</Tag>;
            case ReportFileTypeEnum.PARCEL_DOWNLOAD:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelDownload')}</Tag>;
            case ReportFileTypeEnum.PARCEL_SHIPPING_LABEL_PDF:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelShippingLabelPdf')}</Tag>;
            case ReportFileTypeEnum.COOPERATION_WAREHOUSE_BLACKLIST_DOWNLOAD:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.cooperationWarehouseBlacklistDownload')}</Tag>;
            case ReportFileTypeEnum.PICKUP_TASK:
                return <Tag color="blue">{t('enum.reportTypeOptions.pickupTaskDownload')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportTypeOptions.other')}</Tag>;
        }
    };

    const getStatusTag = (status: number) => {
        switch (status) {
            case ReportFileStatusEnum.DOWNLOADING:
                return <Tag color="orange">{t('enum.reportFileStatusOptions.downloading')}</Tag>;
            case ReportFileStatusEnum.COMPLETED:
                return <Tag color="success">{t('enum.reportFileStatusOptions.completed')}</Tag>;
            case ReportFileStatusEnum.FAILED:
                return <Tag color="error">{t('enum.reportFileStatusOptions.failed')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportFileStatusOptions.other')}</Tag>;
        }
    };

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('YYYY-MM-DD');
    };

    const columns: ColumnsType<ReportFileType> = [
        {
            title: t('central.download.columns.reportDate'),
            dataIndex: 'reportDate',
            key: 'reportDate',
            width: 120,
            align: 'left',
            render: (dateString: string) => formatDate(dateString),
        },
        {
            title: t('central.download.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: 100,
            align: 'left',
            render: (type: number) => getReportFileTypeTag(type),
        },
        {
            title: t('central.download.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'left',
            render: (status: number) => getStatusTag(status),
        },
        {
            title: t('central.download.columns.reportName'),
            dataIndex: 'name',
            key: 'name',
            width: 420,
            align: 'left',
            render: (name?: string) => (
                <Tooltip title={name || '-'}>
                    <Typography.Text ellipsis>
                        {name || '-'}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: t('central.download.filters.operator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 150,
            align: 'left',
            render: (operatorName?: string) => operatorName,
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: ReportFileType) => ({
            disabled: record.status === ReportFileStatusEnum.DOWNLOADING || record.status === ReportFileStatusEnum.FAILED, // 下载中和下载失败不允许选中
        }),
    };

    return (
        <Table
            columns={columns}
            dataSource={reportFileData}
            rowKey="id"
            loading={loading}
            rowSelection={rowSelection}
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="middle"
            className="mb-4"
        />
    );
};

export default CentralReportFileColumns;
